/*
 * Journey step: choose your hardware.
 *
 * Deliberately NOT a reimplementation of the hardware calculator. It reads the
 * same two shop metafields the standalone calculator reads:
 *
 *   custom_calculator.settings_v2         33 rules, filtered by type and options
 *   custom_calculator.product_details_v2  how a computed amount becomes line items
 *
 * so the merchant keeps one rule set. Editing the metafields updates the
 * calculator and this step together. Which keys are read is a section setting,
 * because the unsuffixed v1 pair still exists on this shop and reading it would
 * quote a calculator nobody uses, plausibly and silently.
 *
 * The resolution types below mirror the calculator's behaviour exactly. Where
 * this file and sections/custom-calculator-v2.liquid disagree, that is a bug
 * here, not a difference of opinion.
 *
 * What the customer does not get asked again: area type, length, width, ends
 * and end dimensions all come from the measure step, in feet. Only the things
 * measure cannot know are asked here: materials, build style and post count.
 */
(function () {
  'use strict';

  /**
   * Evaluate a merchant-authored arithmetic expression with the measurement
   * variables substituted in.
   *
   * The standalone calculator calls eval() on these directly. This does not.
   * The metafield is merchant data, but it reaches the browser as page content,
   * so the expression is whitelisted to arithmetic before it is evaluated.
   */
  function evaluate(expression, vars) {
    if (typeof expression !== 'string') return NaN;

    var substituted = expression.replace(/\b(we1|he1|we2|he2|l|w|p|s)\b/g, function (name) {
      var value = vars[name];
      return typeof value === 'number' && isFinite(value) ? String(value) : '0';
    });

    /*
     * These strings come from a shop metafield, which is merchant-editable, so
     * they are evaluated only after everything that is not arithmetic has been
     * proved absent.
     *
     * A short list of Math helpers is allowed because the rules genuinely use
     * them: the per-wall multi_products formulas are written as
     * "Math.ceil(l / 1.5)". They are removed before the check rather than
     * added to the character class, so the guard still rejects every OTHER
     * identifier: anything like fetch, window or constructor leaves letters
     * behind and fails.
     */
    var guard = substituted.replace(/Math\.(ceil|floor|round|min|max)\b/g, '');
    if (!/^[0-9+\-*/(),. ]+$/.test(guard)) return NaN;

    try {
      // eslint-disable-next-line no-new-func
      var result = new Function('return (' + substituted + ');')();
      return typeof result === 'number' && isFinite(result) ? result : NaN;
    } catch (err) {
      return NaN;
    }
  }

  /**
   * Turn one { id, calc } into line items, using the product's detail entry.
   * Mirrors the calculator's four strategies.
   *
   * @returns {Array<{handle:string, sku:string, quantity:number, raw:number}>}
   */
  function resolve(entry, detail, vars) {
    var raw = Math.ceil(evaluate(entry.calc, vars));
    if (!isFinite(raw) || raw <= 0) return [];

    var out = [];

    if (detail.type === 'general') {
      out.push({ handle: detail.handle, sku: detail.sku, quantity: raw, raw: raw });
    } else if (detail.type === 'calc_quant') {
      // Smallest bracket the amount fits under wins.
      var chosen = 0;
      var brackets = detail.calc_quant.slice().sort(function (a, b) {
        return b.less_than - a.less_than;
      });
      for (var b = 0; b < brackets.length; b++) {
        if (raw < brackets[b].less_than) chosen = brackets[b].quant;
      }
      if (chosen > 0) out.push({ handle: detail.handle, sku: detail.sku, quantity: chosen, raw: raw });
    } else if (detail.type === 'move_up') {
      /*
       * Step up to a bigger pack rather than buying more of a small one.
       *
       * Mirrors the fixed handler in sections/custom-calculator-v2.liquid. The
       * AU original assumed every tier was one pack bought once, which held on
       * AU data and is wrong here in two ways:
       *
       *   - A tier can be an ARRAY of packs, a combo: the fence brackets go
       *     2pk, 2x2pk, ... then "one 10-pack and two 2-packs". Reading .handle
       *     off the array gives undefined and the line silently vanishes.
       *   - The tier's own `amount` is the quantity. Hardcoding 1 quotes four
       *     brackets where the ladder says eight.
       *
       * Both were live bugs in the calculator until 2026-06-10. Keep this in
       * step with that file: parity is the contract, not a nice-to-have.
       */
      var index = 0;
      for (var m = 0; m < (detail.move_to_next || []).length; m++) {
        if (raw >= detail.move_to_next[m]) index++;
      }

      var tier = detail.multi_quant[Math.min(index, detail.multi_quant.length - 1)];
      var packs = Array.isArray(tier) ? tier : [tier];

      for (var pk = 0; pk < packs.length; pk++) {
        var pack = packs[pk];
        if (!pack || !pack.handle) continue;
        out.push({
          handle: pack.handle,
          sku: pack.sku,
          quantity: pack.amount > 0 ? pack.amount : 1,
          raw: raw
        });
      }
    } else if (detail.type === 'tabulate') {
      // Greedy pack, biggest first.
      var remaining = raw;
      var packs = detail.multi_quant.slice().sort(function (a, b) {
        return b.amount - a.amount;
      });
      for (var t = 0; t < packs.length; t++) {
        var count = Math.floor(remaining / packs[t].amount);
        if (count > 0) {
          out.push({ handle: packs[t].handle, sku: packs[t].sku, quantity: count, raw: raw });
          remaining -= count * packs[t].amount;
        }
      }
    } else if (detail.type === 'always') {
      out.push({ handle: detail.handle, sku: detail.sku, quantity: detail.always_quant || 1, raw: raw });
    } else if (detail.type === 'over_add' && detail.over_add) {
      if (raw >= detail.over_add.over) {
        out.push({ handle: detail.handle, sku: detail.sku, quantity: detail.over_add.quant, raw: raw });
      }
    }

    return out;
  }

  /** Alley pairs are stored one way round only, so normalise before matching. */
  function alleyMaterials(m1, m2) {
    var a = (m1 || '').replace('other', 'colorbond');
    var b = (m2 || '').replace('other', 'colorbond');
    var key = a + '-' + b;

    var swaps = {
      'colorbond-brick': 'brick-colorbond',
      'colorbond-wood': 'wood-colorbond',
      'brick-wood': 'wood-brick'
    };

    return swaps[key] || key;
  }

  function Hardware(root) {
    this.root = root;
    this.config = this.readConfig();
    if (!this.config) return;

    this.stepId = this.config.stepId || 'hardware';
    this.measureStepId = this.config.measureStepId || 'measure';
    this.labels = this.config.labels || {};
    this.unit = this.config.units === 'm' ? 'm' : 'ft';

    this.introEl = root.querySelector('[data-hw-intro]');
    this.questionsEl = root.querySelector('[data-hw-questions]');
    this.resultEl = root.querySelector('[data-hw-result]');
    this.rowsEl = root.querySelector('[data-hw-rows]');
    this.totalEl = root.querySelector('[data-hw-total]');
    this.unsupportedEl = root.querySelector('[data-hw-unsupported]');

    this.answers = {};
    this.overrides = {};
    this.journey = null;

    this.money = this.moneyFormatter();
    this.bind();
  }

  Hardware.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-hw-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  Hardware.prototype.moneyFormatter = function () {
    var currency = this.config.currency || 'USD';
    try {
      // Pinned to the store's locale, not the visitor's. Left as undefined,
      // a visitor browsing from outside the US sees "USD 199.50" where the
      // rest of the storefront says "$199.50", because Intl falls back to a
      // currency code when the locale and currency disagree.
      var fmt = new Intl.NumberFormat(this.config.locale || 'en-US', { style: 'currency', currency: currency });
      return function (cents) {
        return fmt.format(cents / 100);
      };
    } catch (err) {
      return function (cents) {
        return '$' + (cents / 100).toFixed(2);
      };
    }
  };

  Hardware.prototype.bind = function () {
    var self = this;

    if (window.CatnetsJourney) {
      this.attach(window.CatnetsJourney);
    } else {
      document.addEventListener('journey:ready', function (e) {
        self.attach(e.detail.journey);
      });
    }

    document.addEventListener('journey:step:enter', function (e) {
      if (e.detail.step === self.stepId) self.refresh();
    });

    document.addEventListener('journey:step:complete', function (e) {
      // Measurements moved, so the hardware list is stale.
      if (e.detail.step === self.measureStepId) {
        self.overrides = {};
        self.refresh();
      }
    });

    this.root.addEventListener('change', function (e) {
      if (e.target.matches('[data-hw-answer]')) {
        self.answers[e.target.getAttribute('data-hw-answer')] = e.target.value;
        self.overrides = {};
        self.renderQuestions();
        self.renderResult();
      }
    });

    this.root.addEventListener('input', function (e) {
      if (e.target.matches('[data-hw-qty]')) {
        var key = e.target.getAttribute('data-hw-qty');
        var value = parseInt(e.target.value, 10);
        self.overrides[key] = isFinite(value) && value >= 0 ? value : 0;
        self.renderTotals();
        self.report();
      }
    });
  };

  Hardware.prototype.attach = function (journey) {
    this.journey = journey;

    var saved = journey.data(this.stepId);
    if (saved && saved.answers) {
      this.answers = saved.answers;
      this.overrides = saved.overrides || {};
    }

    this.refresh();
  };

  Hardware.prototype.measure = function () {
    return this.journey ? this.journey.data(this.measureStepId) : null;
  };

  /** Rules for the measured area type. Empty means we cannot spec this area. */
  /**
   * An area can borrow another area's rule set without the metafields knowing.
   * Kurt's call: a backyard is hardware-wise identical to a balcony, four
   * surfaces and a net across the top, so "backyard:balcony" (the default)
   * makes backyard use the balcony rules. The standalone calculator and its
   * metafields are untouched; the alias exists only in this step.
   *
   * Config format: "backyard:balcony, other-area:existing-type".
   */
  Hardware.prototype.ruleType = function (area) {
    if (!this.aliases) {
      this.aliases = {};
      var raw = String(this.config.areaAliases || '');
      var pairs = raw.split(',');
      for (var i = 0; i < pairs.length; i++) {
        var bits = pairs[i].split(':');
        if (bits.length === 2) {
          this.aliases[bits[0].trim()] = bits[1].trim();
        }
      }
    }
    return this.aliases[area] || area;
  };

  Hardware.prototype.rulesForType = function (type) {
    var out = [];
    for (var i = 0; i < this.config.rules.length; i++) {
      if (this.config.rules[i].type === type) out.push(this.config.rules[i]);
    }
    return out;
  };

  /**
   * Which questions apply, given the area type and what has been answered.
   * Style only appears where an economical build is actually offered.
   */
  Hardware.prototype.questions = function (area) {
    var out = [];
    var materials = this.config.materialOptions;
    var type = this.ruleType(area);
    var aliased = type !== area;

    if (type === 'alley') {
      // Named surfaces, matching the standalone calculator's "Fence Side" and
      // "House Side". Generic "side 1 / side 2" left customers guessing which
      // wall was which, and material drives the fixings.
      out.push({ id: 'm1', label: this.labels.alleyFence || 'What is the fence side made of?', options: materials });
      out.push({ id: 'm2', label: this.labels.alleyHouse || 'What is the house side made of?', options: materials });
    } else if (type === 'balcony') {
      if (aliased) {
        // An aliased area (backyard) has four sides rather than walls, roof
        // and floor. Same maths either way: m1/m2 weigh in at the width and
        // m3/m4 at the length, which for a backyard is its two short sides
        // and two long sides.
        out.push({ id: 'm1', label: this.labels.side1 || 'Side 1 material', options: materials });
        out.push({ id: 'm2', label: this.labels.side2 || 'Side 2 material', options: materials });
        out.push({ id: 'm3', label: this.labels.side3 || 'Side 3 material', options: materials });
        out.push({ id: 'm4', label: this.labels.side4 || 'Side 4 material', options: materials });
      } else {
        out.push({ id: 'm1', label: this.labels.balconySide1 || 'What is side wall 1 made of?', options: materials });
        out.push({ id: 'm2', label: this.labels.balconySide2 || 'What is side wall 2 made of?', options: materials });
        out.push({ id: 'm3', label: this.labels.roof || 'Roof material', options: materials });
        out.push({ id: 'm4', label: this.labels.floor || 'Floor material', options: materials });
      }
    } else if (type === 'fenceline') {
      out.push({ id: 'm1', label: this.labels.fence || 'Fence material', options: materials });
      out.push({
        id: 'p',
        label: this.labels.posts || 'How many Fence Extension Brackets?',
        help: this.labels.postsHelp || '',
        type: 'number',
        min: 1,
        max: 200
      });
    }

    if (this.styleOffered(type)) {
      out.push({
        id: 'style',
        label: this.labels.style || 'Build style',
        // The calculator explains the two methods next to the same question.
        // Nobody outside the trade knows what "rope edging" means, and this is
        // the point where the customer has to pick one.
        help: this.labels.styleHelp || '',
        options: this.config.styleOptions
      });
    }

    return out;
  };

  /**
   * Economical builds exist only where there is wood to fix into. Alley needs
   * one wooden side, balcony needs all four surfaces wooden. Fenceline has no
   * style choice at all.
   */
  Hardware.prototype.styleOffered = function (type) {
    var a = this.answers;

    if (type === 'alley') return a.m1 === 'wood' || a.m2 === 'wood';
    if (type === 'balcony') {
      return a.m1 === 'wood' && a.m2 === 'wood' && a.m3 === 'wood' && a.m4 === 'wood';
    }
    return false;
  };

  Hardware.prototype.effectiveStyle = function (type) {
    if (!this.styleOffered(type)) return 'professional';
    return this.answers.style || 'economical';
  };

  /** Find the single rule matching the type and the answered options. */
  Hardware.prototype.matchRule = function (measure) {
    var type = this.ruleType(measure.area);
    var rules = this.rulesForType(type);
    if (!rules.length) return null;

    var a = this.answers;
    var style = this.effectiveStyle(type);
    var wanted;

    if (type === 'alley') {
      if (!a.m1 || !a.m2) return null;
      wanted = [measure.ends === 'one' ? 'one-end' : measure.ends === 'two' ? 'two-ends' : 'no-ends',
        alleyMaterials(a.m1, a.m2), style];
    } else if (type === 'balcony') {
      if (!a.m1 || !a.m2 || !a.m3 || !a.m4) return null;
      var allWood = a.m1 === 'wood' && a.m2 === 'wood' && a.m3 === 'wood' && a.m4 === 'wood';
      wanted = [allWood ? 'wood' : 'multi', style];
    } else if (type === 'fenceline') {
      if (!a.m1) return null;
      wanted = [(a.m1 || '').replace('other', 'colorbond')];
    } else {
      return null;
    }

    for (var i = 0; i < rules.length; i++) {
      var opts = rules[i].options;
      var same = opts.length === wanted.length;
      for (var o = 0; o < opts.length && same; o++) {
        if (opts[o] !== wanted[o]) same = false;
      }
      if (same) return rules[i];
    }

    return null;
  };

  Hardware.prototype.detail = function (id) {
    for (var i = 0; i < this.config.details.length; i++) {
      if (this.config.details[i].id === id) return this.config.details[i];
    }
    return null;
  };

  Hardware.prototype.product = function (handle) {
    return this.config.products[handle] || null;
  };

  /**
   * The variant a rule's SKU refers to, falling back to the product's default.
   *
   * Products published before the section started emitting a variants array
   * still work: they just resolve to the default, which is what they did
   * before. An unrecognised SKU does the same rather than dropping the line,
   * because a missing fixing is worse than a mispriced one and the fallback is
   * visible on screen.
   */
  function variantForSku(product, sku) {
    var list = product.variants || [];

    for (var v = 0; v < list.length; v++) {
      if (sku && list[v].sku === sku) return list[v];
    }

    return {
      id: product.variantId,
      title: '',
      sku: product.sku,
      price: product.price,
      available: product.available
    };
  }

  /** The full hardware list for the current answers. */
  Hardware.prototype.build = function () {
    var measure = this.measure();
    if (!measure) return null;

    var rule = this.matchRule(measure);
    if (!rule) return null;

    var posts = parseFloat(this.answers.p);

    // Post count is a real input to the fenceline maths, not an optional extra.
    // Without it the calcs still evaluate, using zero, and quietly produce a
    // hardware list that is wrong rather than obviously incomplete.
    if (this.needsPosts(this.ruleType(measure.area)) && !(posts > 0)) return null;

    var vars = {
      l: measure.length || 0,
      w: measure.cover === 'height' ? measure.height || 0 : measure.width || 0,
      p: posts > 0 ? posts : 0,
      we1: measure.end1 ? measure.end1.width : 0,
      he1: measure.end1 ? measure.end1.height : 0,
      we2: measure.end2 ? measure.end2.width : 0,
      he2: measure.end2 ? measure.end2.height : 0
    };

    var items = [];
    var entries = rule.products.slice();

    /*
     * Mixed-material rules carry `multi_products`: per-material fixings, one
     * record per material, each holding a formula PER WALL:
     *
     *   { selection_type: "wood", id: "screw-eyelets-c",
     *     calc: { m1: "Math.ceil(w / 1.5)", ..., m3: "Math.ceil(l / 1.5)" } }
     *
     * So each wall is evaluated with its own formula and the results are summed
     * per material. The calc is an OBJECT, not a string. Treating it as one
     * (`multi.calc + ' * ' + total`) yields the literal "[object Object] * 12",
     * which evaluates to NaN, and NaN quietly falls through to the top of the
     * pack ladder. That is the "eyelets always return the maximum" bug JM
     * reported on the balcony; it was fixed in the calculator on 2026-06-10 and
     * this mirrors that fix.
     *
     * The result is a COUNT of fixings, not a length, which is why these
     * records (`*-c`) have count-keyed ladders rather than the length-banded
     * ones used elsewhere. Feeding a count into a length ladder under-quotes.
     */
    if (rule.multi_products && rule.multi_products.length) {
      var a = this.answers;
      var walls = [
        { key: 'm1', material: a.m1 },
        { key: 'm2', material: a.m2 },
        { key: 'm3', material: a.m3 },
        { key: 'm4', material: a.m4 }
      ];
      var totals = {};

      for (var wl = 0; wl < walls.length; wl++) {
        var wall = walls[wl];
        if (!wall.material) continue;

        // "Something else" is priced as colorbond, same as the calculator.
        var selType = wall.material === 'other' ? 'colorbond' : wall.material;

        var record = null;
        for (var f = 0; f < rule.multi_products.length; f++) {
          if (rule.multi_products[f].selection_type === selType) record = rule.multi_products[f];
        }
        if (!record || !record.calc || record.calc[wall.key] == null) continue;

        var wallQty = evaluate(record.calc[wall.key], vars);
        if (isFinite(wallQty)) totals[selType] = (totals[selType] || 0) + wallQty;
      }

      for (var mp = 0; mp < rule.multi_products.length; mp++) {
        var multi = rule.multi_products[mp];
        var total = Math.ceil(totals[multi.selection_type] || 0);
        if (total > 0) entries.push({ id: multi.id, calc: String(total) });
      }
    }

    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var detail = this.detail(entry.id);
      if (!detail) continue;

      var lines = resolve(entry, detail, vars);

      for (var li = 0; li < lines.length; li++) {
        var line = lines[li];
        var product = this.product(line.handle);
        if (!product) continue;

        // The rules choose a SKU, not a product. One handle can carry a 5-pack
        // and a 10-pack, and a single tier can ask for both at once, so resolve
        // the variant by SKU and fall back to the product's default only when
        // the SKU is unknown.
        var variant = variantForSku(product, line.sku);
        var title = product.title;
        if (variant.title && variant.title !== 'Default Title') title += ' - ' + variant.title;

        // Overrides are keyed by handle AND sku for the same reason: keyed by
        // handle alone, editing the 10-pack would silently edit the 5-pack too.
        var key = line.handle + '|' + (variant.sku || '');
        var quantity = this.overrides.hasOwnProperty(key) ? this.overrides[key] : line.quantity;

        items.push({
          id: entry.id,
          handle: line.handle,
          sku: variant.sku,
          key: key,
          title: title,
          image: product.image,
          url: product.url,
          variantId: variant.id,
          price: variant.price,
          suggested: line.quantity,
          quantity: quantity,
          total: variant.price * quantity,

          /*
           * Whether the storefront can actually sell this right now.
           *
           * The rules name a product; they know nothing about stock. Shopify's
           * /cart/add.js rejects the WHOLE payload if any single line is
           * unavailable, so one sold-out fixing used to fail the entire build
           * with a generic error. Edging rope going out of stock broke every
           * one of the 33 scenarios that way.
           *
           * Unavailable lines stay visible, because a missing fixing means the
           * build does not work and the customer needs to know, but they are
           * kept out of the cart and out of the total.
           */
          available: variant.available !== false
        });
      }
    }

    return { rule: rule.name, vars: vars, items: items };
  };

  Hardware.prototype.needsPosts = function (type) {
    return type === 'fenceline';
  };

  Hardware.prototype.refresh = function () {
    if (!this.journey) return;

    var measure = this.measure();

    if (!measure) return;

    var supported = this.rulesForType(this.ruleType(measure.area)).length > 0;

    if (this.unsupportedEl) {
      this.unsupportedEl.hidden = supported;
      if (!supported) {
        this.unsupportedEl.textContent = (this.labels.unsupported || 'We cannot spec hardware for this area automatically.');
      }
    }

    if (this.questionsEl) this.questionsEl.hidden = !supported;
    if (this.resultEl) this.resultEl.hidden = !supported;

    if (!supported) {
      // Nothing to sell here, so let the customer past rather than trapping them.
      if (this.journey.data(this.stepId)) this.journey.clear(this.stepId);
      return;
    }

    this.renderIntro(measure);
    this.renderQuestions();
    this.renderResult();
  };

  Hardware.prototype.renderIntro = function (measure) {
    if (!this.introEl) return;

    var drop = measure.cover === 'height' ? measure.height : measure.width;
    var bits = ['Using your ' + measure.length + this.unit + ' by ' + drop + this.unit + ' ' +
      (measure.areaLabel || '').toLowerCase()];

    if (measure.ends === 'one') bits.push('with one enclosed end');
    if (measure.ends === 'two') bits.push('with two enclosed ends');

    this.introEl.textContent = bits.join(', ') + '. No need to measure again.';
    this.introEl.hidden = false;
  };

  Hardware.prototype.renderQuestions = function () {
    if (!this.questionsEl) return;

    var measure = this.measure();
    var questions = this.questions(measure.area);
    var html = '';

    for (var i = 0; i < questions.length; i++) {
      var q = questions[i];
      var value = this.answers[q.id] || '';

      html += '<div class="jhw__field"><label class="jhw__label" for="jhw-' + q.id + '">' + q.label + '</label>';

      if (q.help) {
        html += '<p class="jhw__help">' + q.help + '</p>';
      }

      if (q.type === 'number') {
        html +=
          '<div class="jhw__input-wrap"><input class="jhw__input" id="jhw-' + q.id + '" type="number"' +
          ' inputmode="numeric" min="' + q.min + '" max="' + q.max + '"' +
          ' data-hw-answer="' + q.id + '" value="' + value + '"></div>';
      } else {
        html += '<select class="jhw__select" id="jhw-' + q.id + '" data-hw-answer="' + q.id + '">';
        html += '<option value="">' + (this.labels.choose || 'Choose one') + '</option>';
        for (var o = 0; o < q.options.length; o++) {
          var opt = q.options[o];
          html +=
            '<option value="' + opt.value + '"' + (value === opt.value ? ' selected' : '') + '>' +
            opt.label + '</option>';
        }
        html += '</select>';
      }

      html += '</div>';
    }

    this.questionsEl.innerHTML = html;
  };

  Hardware.prototype.renderResult = function () {
    var result = this.build();

    if (!this.rowsEl) return;

    if (!result || !result.items.length) {
      this.rowsEl.innerHTML = '';
      if (this.resultEl) this.resultEl.hidden = true;
      this.report();
      return;
    }

    if (this.resultEl) this.resultEl.hidden = false;

    var html = '';
    for (var i = 0; i < result.items.length; i++) {
      var item = result.items[i];
      var soldOut = item.available === false;

      html +=
        '<div class="jhw__row' + (soldOut ? ' is-soldout' : '') + '" data-hw-row="' + item.handle + '">' +
          (item.image ? '<img class="jhw__row-image" src="' + item.image + '" alt="" loading="lazy">' : '<span class="jhw__row-image"></span>') +
          '<div class="jhw__row-main">' +
            '<a class="jhw__row-title" href="' + item.url + '" target="_blank" rel="noopener">' + item.title + '</a>' +
            (soldOut
              ? '<span class="jhw__row-soldout">' + (this.labels.soldOut || 'Out of stock, not added to your cart') + '</span>'
              : '<span class="jhw__row-price">' + this.money(item.price) + ' each</span>') +
          '</div>' +
          // No quantity control on something that cannot be bought, and no
          // price either: it is not part of what the customer is paying for.
          (soldOut
            ? '<span class="jhw__qty-placeholder"></span>' +
              '<span class="jhw__row-total" data-hw-row-total="' + item.handle + '">&mdash;</span>'
            : '<input class="jhw__qty" type="number" min="0" max="999" value="' + item.quantity + '"' +
                ' data-hw-qty="' + item.key + '" aria-label="Quantity of ' + item.title + '">' +
              '<span class="jhw__row-total" data-hw-row-total="' + item.handle + '">' + this.money(item.total) + '</span>') +
        '</div>';
    }

    this.rowsEl.innerHTML = html;
    this.renderTotals();
    this.report();
  };

  Hardware.prototype.renderTotals = function () {
    var result = this.build();
    if (!result) return;

    var total = 0;
    for (var i = 0; i < result.items.length; i++) {
      var item = result.items[i];
      if (item.available === false) continue; // not bought, so not charged for

      total += item.total;

      var cell = this.root.querySelector('[data-hw-row-total="' + item.handle + '"]');
      if (cell) cell.textContent = this.money(item.total);
    }

    if (this.totalEl) this.totalEl.textContent = this.money(total);
  };

  Hardware.prototype.report = function () {
    if (!this.journey) return;

    var measure = this.measure();
    if (measure && !this.rulesForType(this.ruleType(measure.area)).length) return;

    var result = this.build();
    var items = result ? result.items.filter(function (i) {
      return i.quantity > 0;
    }) : [];

    if (!items.length) {
      if (this.journey.data(this.stepId)) this.journey.clear(this.stepId);
      return;
    }

    // Sold-out lines are reported so the review step can name them, but they
    // are not part of the price, because they are not being bought.
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].available !== false) total += items[i].total;
    }

    this.journey.complete(this.stepId, {
      rule: result.rule,
      answers: this.answers,
      overrides: this.overrides,
      items: items,
      totalPrice: total
    });
  };

  // The raw engine, exposed so the test suite can sweep every rule in the
  // real metafield data through it and fail loudly on anything it cannot
  // handle. This is the parity guarantee with the standalone calculator.
  window.CatnetsHardwareEngine = { evaluate: evaluate, resolve: resolve };

  function boot() {
    var roots = document.querySelectorAll('[data-journey-step-source]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('[data-hw-config]')) new Hardware(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
